"use client";

import Image from "next/image";
import clsx from "clsx";
import CommonTitle from "@/components/common/CommonTitle";
import CommonSubtitle from "@/components/common/CommonSubtitle";
import { GridSectionInfoType } from "@/app/ai/ai-agent/root";

interface AIGridBoxProps {
    sections: GridSectionInfoType[];
}

const AIGridBox = ({ sections }: AIGridBoxProps) => {
    return (
        <>
            {sections.map((section, index) => (
                <section
                    key={index}
                    className={clsx(
                        "relative z-10 overflow-hidden py-12 xl:py-20",
                        section.theme === "white" && "bg-white",
                        section.theme === "gray" && "bg-[#EFEFF6]"
                    )}
                >
                    {section.backgroundImage && (
                        <div
                            className="absolute left-0 top-0 -z-10 h-full w-full"
                            style={{
                                backgroundImage: `url(${section.backgroundImage.src})`,
                                backgroundSize: "cover",
                                backgroundPosition: "center",
                                backgroundRepeat: "no-repeat",
                            }}
                        />
                    )}
                    <div className="container relative z-10 mx-auto px-4">
                        <div className="mx-auto mb-6 xl:mb-8 text-center">
                            <div className={`inline-flex w-fit rounded-full ${section.theme === "white" ? "bg-[#F3F3F3]" : "bg-white"} px-3 py-1 text-sm font-semibold`}>
                                <span className="bg-gradient-to-r from-[#781E8F] to-[#422CBB] bg-clip-text text-transparent">
                                    {section.tag}
                                </span>
                            </div>
                            <CommonTitle
                                as="h2"
                                color={section.theme === "hasImage" ? "#ffffff" : "#202020"}
                                fontSizeClass="text-[24px] xl:text-[32px] mt-2"
                                fontWeightClass="font-bold"
                            >
                                {section.heading}
                            </CommonTitle>
                            {section.subheading && (
                                <CommonSubtitle
                                    color={section.theme === "hasImage" ? "#ffffff" : "#202020"}
                                >
                                    {section.subheading}
                                </CommonSubtitle>
                            )}
                        </div>
                        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 xl:gap-8">
                            {section.gridAreaInfo.map((item, index) => {
                                const isLarge = item.large;
                                return (
                                    <div
                                        key={index}
                                        className={clsx(
                                            "rounded-[16px] transition-all duration-300 overflow-hidden",
                                            section.theme === "white"
                                                ? "bg-gradient-to-r from-[#F4F4FD] to-[#FCFAF9] text-[#202020]"
                                                : "bg-white shadow-sm",
                                            isLarge ? "lg:col-span-2" : "lg:col-span-1"
                                        )}
                                    >
                                        <div
                                            className={clsx(
                                                "h-full p-6 xl:p-8",
                                                isLarge
                                                    ? "flex flex-col lg:flex-row items-center gap-4"
                                                    : "flex flex-col gap-4"
                                            )}
                                        >
                                            <div
                                                className={clsx(
                                                    "",
                                                    isLarge ? "lg:w-1/3" : ""
                                                )}
                                            >
                                                <CommonTitle
                                                    as="h3"
                                                    color="#202020"
                                                    fontSizeClass="text-[18px] lg:text-[20px] mb-2"
                                                    fontWeightClass="font-semibold"
                                                >
                                                    {item.title}
                                                </CommonTitle>
                                                <CommonSubtitle
                                                    fontSizeWeight="min-h-[70px]"
                                                >
                                                    {item.description}
                                                </CommonSubtitle>
                                            </div>
                                            {item.image && (
                                                <div
                                                    className={clsx(
                                                        "relative flex items-center justify-center h-fit",
                                                        isLarge ? "lg:w-2/3" : "mt-auto"
                                                    )}
                                                >
                                                    <Image
                                                        src={item.image}
                                                        alt={item.title}
                                                        className="w-full h-full object-contain"
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                    </div>
                </section>
            ))}
        </>
    );
};

export default AIGridBox;